import { BYCATCH_LABELS } from "./const";

/**
 * Species labels the backend treats as categories rather than taxa. These are the
 * only values rendered upright - anything else is a taxon name and gets italics.
 *
 * The bare three are what the detector and the classifier write, as category_str
 * and as classification_classname. The named bycatch verdicts come from the
 * dropdown instead, and are equally not taxa - "Black Flies" names a group of
 * insects, not a species, and nothing in the label says it is bycatch.
 *
 * Keyed by lowercase spelling, because the producers disagree about case:
 * category_str is lowercase, the dropdown's labels are not.
 */
const CATEGORY_LABELS: Record<string, string> = {
  mosquito: "Mosquito",
  bycatch: "Bycatch",
  debris: "Debris",
  ...Object.fromEntries(
    BYCATCH_LABELS.map((label) => [label.toLowerCase(), label]),
  ),
};

/**
 * Categories worth reporting as having been overridden. "Mosquito" is what the
 * detector gives everything it does not reject, so a species contributed on top
 * of it replaces no real verdict and there is nothing to reveal on hover.
 */
const OVERRIDABLE_CATEGORIES = ["bycatch", "debris"];

/** The named bycatch verdicts, lowercased, for the label -> category rule below. */
const BYCATCH_LABELS_LOWER = new Set(
  BYCATCH_LABELS.map((label) => label.toLowerCase()),
);

/**
 * Canonical display form: first letter capitalized, everything after it lowercase,
 * with whitespace trimmed and collapsed - "Aedes albopictus".
 *
 * The backend already normalizes contributed_species on write
 * (normalizeSpeciesName in scout-web-backend), but algorithm_taxonomy and
 * category_str come from other producers, so the rule is re-applied here.
 *
 * A label that is not a taxon is looked up rather than recased, for the same
 * reason the backend's twin does: binomial case would render
 * "Horse and Deer Flies (Tabanidae)" as "Horse and deer flies (tabanidae)".
 */
export const normalizeSpeciesName = (species: string): string => {
  const trimmed = species.trim().replace(/\s+/g, " ");
  if (!trimmed) return "";
  return (
    CATEGORY_LABELS[trimmed.toLowerCase()] ??
    trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
  );
};

/** A label plus how it should be rendered. */
interface SpeciesLabel {
  label: string;
  /** Taxon names are italicized; the mosquito/bycatch/debris categories are not. */
  isItalic: boolean;
}

/** Who contributed the label, from the viewer's point of view. */
export type Contributor = "you" | "colleague";

export interface SpeciesDisplay extends SpeciesLabel {
  /**
   * Set only when the displayed label is a human contribution rather than Scout's
   * own, and then to whether the viewer is the one who made it.
   */
  contributor?: Contributor;
  /**
   * What the specimen read as before a human contributed a species, set only when
   * that differs from what is displayed - the header shows it as "Scout said ...".
   */
  overridden?: SpeciesLabel;
}

const sameEmail = (
  a: string | null | undefined,
  b: string | null | undefined,
) => Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());

/**
 * How a stored label should read on screen.
 *
 * A bycatch verdict usually names the insect and nothing else - "Black Flies" - because
 * the dropdown files it under a "Bycatch" heading and repeating the word in every option
 * would be noise. Out here there is no heading, and "Black Flies" on its own gives no
 * hint that Scout counted the specimen as bycatch, so the category goes back in front
 * of it. A label that already names the category is left as it is, so the catch-all
 * "Other/Unknown Bycatch" reads as itself rather than stuttering into
 * "Bycatch - Other/Unknown Bycatch", and any later verdict spelled with the word in it
 * gets the same treatment without anyone having to remember this.
 *
 * Only for display: the stored value stays the bare name, which is what every comparison
 * in this module and every species query in the backend matches on.
 */
const describe = (label: string): SpeciesLabel => {
  const lower = label.toLowerCase();
  return {
    label:
      BYCATCH_LABELS_LOWER.has(lower) && !lower.includes("bycatch")
        ? `Bycatch - ${label}`
        : label,
    isItalic: !(lower in CATEGORY_LABELS),
  };
};

/**
 * The category a label implies, or null when the label is not a verdict - a blank
 * label, or the detection library's `class_<n>` placeholder. Any other real label
 * is a species, and every species is a mosquito. Mirrors categoryForLabel in the
 * backend's src/utils/categories.ts, including its treatment of every named
 * bycatch verdict as the same verdict as the bare "bycatch".
 */
const categoryForLabel = (label: string | null | undefined): string | null => {
  const trimmed = (label ?? "").trim().toLowerCase();
  if (!trimmed) return null;
  if (/^class_\d+$/.test(trimmed.replace(/\s+/g, "_"))) return null;
  if (BYCATCH_LABELS_LOWER.has(trimmed)) return "bycatch";
  return OVERRIDABLE_CATEGORIES.includes(trimmed) ? trimmed : "mosquito";
};

/**
 * The category the specimen carried before anyone contributed a species, when that
 * category was a real verdict rather than the mosquito default.
 *
 * category_str cannot answer this on its own: it is the *effective* category, and a
 * contribution rewrites it - a species on a bycatch row flips it to "mosquito"
 * (effectiveCategoryExpr in the backend's updateDetection). So the precedence below
 * is the backend's effectiveCategory with contributed_species dropped: the
 * classifier's verdict, else the detector's, else the stored category for rows
 * predating the migration that split original_category_str out.
 */
const overriddenCategory = (detection: {
  category_str?: string | null;
  original_category_str?: string | null;
  classification_classname?: string | null;
}): string => {
  const category =
    categoryForLabel(detection.classification_classname) ??
    (detection.original_category_str ?? detection.category_str ?? "")
      .trim()
      .toLowerCase();
  return OVERRIDABLE_CATEGORIES.includes(category)
    ? normalizeSpeciesName(category)
    : "";
};

/**
 * A detection's species is its contributed_species if it has one, otherwise its
 * algorithm_taxonomy - the same precedence as effectiveSpecies in the backend.
 * With neither, the detector's category is all there is to show.
 */
export const getSpeciesDisplay = (
  detection: {
    contributed_species?: string | null;
    algorithm_taxonomy?: string | null;
    category_str?: string | null;
    original_category_str?: string | null;
    classification_classname?: string | null;
    updated_by?: string | null;
  },
  viewerEmail?: string | null,
): SpeciesDisplay | undefined => {
  const contributed = normalizeSpeciesName(detection.contributed_species ?? "");
  const algorithm = normalizeSpeciesName(detection.algorithm_taxonomy ?? "");
  const label =
    contributed ||
    algorithm ||
    normalizeSpeciesName(detection.category_str ?? "");

  if (!label) return undefined;

  // The classifier's species if it produced one, otherwise the category it or the
  // detector settled on. Normalized on both sides, so a recapitalization of the
  // classifier's own label does not read as an override.
  const prior = algorithm || overriddenCategory(detection);

  // A named bycatch verdict sits inside the category Scout already gave the row,
  // so "Scout said Bycatch" beside "Bycatch - Black Flies" would be reporting
  // agreement. Only a label that moves the specimen to a *different* category
  // overrode anything.
  const refinesPriorCategory =
    OVERRIDABLE_CATEGORIES.includes(prior.toLowerCase()) &&
    categoryForLabel(label) === prior.toLowerCase();

  return {
    ...describe(label),
    // updated_by is the last person to write the row, which is the contributor
    // for every path that sets a species. It is also stamped by a bookmark or a
    // notes edit, so a teammate touching the specimen afterwards reattributes the
    // label to them - the backend has no dedicated contributed_by to key off.
    contributor: contributed
      ? sameEmail(detection.updated_by, viewerEmail)
        ? "you"
        : "colleague"
      : undefined,
    overridden:
      prior && prior !== label && !refinesPriorCategory
        ? describe(prior)
        : undefined,
  };
};
